// /js/pages/catalog.js
"use strict";

import { onReady } from '../core/ready.js';
import rangeSlider from 'range-slider-input';

// ==========================
// Utils
// ==========================
function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    } catch (err) {
        return value;
    }
}

function toIntList(input) {
    if (!input) return [];
    const parts = Array.isArray(input) ? input : [input];
    return parts
        .flatMap(v => safeDecode(String(v || '')).split(','))
        .map(v => parseInt(v, 10))
        .filter(Number.isFinite);
}

function toStrList(input) {
    if (!input) return [];
    const parts = Array.isArray(input) ? input : [input];
    return parts
        .flatMap(v => safeDecode(String(v || '')).split(','))
        .map(v => (v || '').trim())
        .filter(Boolean);
}

function normalizeHex(value) {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const prefixed = raw.startsWith('#') ? raw : `#${raw}`;
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(prefixed) ? prefixed : '';
}

function isLightHex(hex) {
    const clean = normalizeHex(hex).replace('#', '');
    if (!clean) return false;
    const full = clean.length === 3
        ? clean.split('').map(c => c + c).join('')
        : clean;
    const num = parseInt(full, 16);
    if (!Number.isFinite(num)) return false;
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance > 0.6;
}

function getParamValues(params, name) {
    if (!params || !name) return [];
    const many = params.getAll(name);
    if (many && many.length) return many;
    const single = params.get(name);
    return single ? [single] : [];
}

function formatResultsText(template, count) {
    const tpl = template || '%d risultati';
    const safeCount = Number.isFinite(count) ? count : 0;
    return tpl.replace('%d', safeCount);
}

function updateResultsCounter(count) {
    const counter = document.getElementById('catalogResultsCounter');
    if (!counter) return;
    const parsed = Number.isFinite(count) ? count : 0;
    counter.textContent = formatResultsText(counter.dataset.template, parsed);
    counter.style.display = '';
}

function hideResultsCounter() {
    const counter = document.getElementById('catalogResultsCounter');
    if (!counter) return;
    counter.style.display = 'none';
}

function initResultsCounter(active = false) {
    if (!active) {
        hideResultsCounter();
    }
}

function parsePriceValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const normalized = String(value).trim().replace(/\s+/g, '').replace(',', '.');
    if (normalized === '' || Number.isNaN(Number(normalized))) return null;
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return Number(parsed.toFixed(2));
}

function formatPriceValue(value) {
    const parsed = parsePriceValue(value);
    if (!Number.isFinite(parsed)) return '';
    return `${parsed.toLocaleString('it-IT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    })}€`;
}

let PRICE_RANGE_SLIDER = null;
let PRICE_RANGE_SYNCING = false;
let PRICE_RANGE_CHANGE_TIMEOUT = null;
const PRICE_RANGE_APPLY_DEBOUNCE = 140;

function getPriceFilterElements() {
    return {
        root: document.getElementById('catalogPriceFilter'),
        slider: document.getElementById('priceRangeSlider'),
        minInput: document.getElementById('priceRangeMinInput'),
        maxInput: document.getElementById('priceRangeMaxInput'),
        minLabel: document.getElementById('priceRangeMinLabel'),
        maxLabel: document.getElementById('priceRangeMaxLabel'),
        emptyLabel: document.getElementById('priceRangeEmptyLabel')
    };
}

function resetPriceRangeLabelPositions() {
    const { minLabel, maxLabel } = getPriceFilterElements();
    if (minLabel) {
        minLabel.style.left = '0%';
        minLabel.style.transform = 'translateX(0)';
    }
    if (maxLabel) {
        maxLabel.style.left = '100%';
        maxLabel.style.transform = 'translateX(-100%)';
    }
}

function toPricePercent(value, minBound, maxBound) {
    if (!Number.isFinite(value) || !Number.isFinite(minBound) || !Number.isFinite(maxBound) || maxBound <= minBound) {
        return 0;
    }
    const raw = ((value - minBound) / (maxBound - minBound)) * 100;
    return Math.max(0, Math.min(raw, 100));
}

function positionPriceValueLabel(label, percent) {
    if (!label) return;
    const safePercent = Math.max(0, Math.min(percent, 100));
    let transform = 'translateX(-50%)';
    if (safePercent <= 4) {
        transform = 'translateX(0)';
    } else if (safePercent >= 96) {
        transform = 'translateX(-100%)';
    }
    label.style.left = `${safePercent}%`;
    label.style.transform = transform;
}

function clampPriceRangePair(minValue, maxValue, minBound, maxBound) {
    let minSelected = parsePriceValue(minValue);
    let maxSelected = parsePriceValue(maxValue);

    if (!Number.isFinite(minSelected)) minSelected = minBound;
    if (!Number.isFinite(maxSelected)) maxSelected = maxBound;

    minSelected = Math.max(minBound, Math.min(minSelected, maxBound));
    maxSelected = Math.max(minBound, Math.min(maxSelected, maxBound));
    if (minSelected > maxSelected) {
        maxSelected = minSelected;
    }
    return [
        Number(minSelected.toFixed(2)),
        Number(maxSelected.toFixed(2))
    ];
}

function syncPriceRangeHiddenInputs(minValue, maxValue) {
    const { minInput, maxInput } = getPriceFilterElements();
    if (!minInput || !maxInput) return;
    minInput.value = Number(minValue).toFixed(2);
    maxInput.value = Number(maxValue).toFixed(2);
}

function syncPriceRangeSliderValues(minValue, maxValue) {
    syncPriceRangeHiddenInputs(minValue, maxValue);
    syncPriceRangeLabels(minValue, maxValue);
    if (!PRICE_RANGE_SLIDER) return;
    PRICE_RANGE_SYNCING = true;
    PRICE_RANGE_SLIDER.value([minValue, maxValue]);
    PRICE_RANGE_SYNCING = false;
}

function setPriceRangeBounds(minBound, maxBound) {
    const { minInput, maxInput } = getPriceFilterElements();
    if (!minInput || !maxInput) return;
    minInput.min = Number(minBound).toFixed(2);
    minInput.max = Number(maxBound).toFixed(2);
    maxInput.min = Number(minBound).toFixed(2);
    maxInput.max = Number(maxBound).toFixed(2);
    if (!PRICE_RANGE_SLIDER) return;
    PRICE_RANGE_SYNCING = true;
    PRICE_RANGE_SLIDER.min(minBound);
    PRICE_RANGE_SLIDER.max(maxBound);
    PRICE_RANGE_SLIDER.step(0.01);
    PRICE_RANGE_SYNCING = false;
}

function setPriceRangeDisabled(disabled) {
    const { minInput, maxInput } = getPriceFilterElements();
    if (minInput) minInput.disabled = !!disabled;
    if (maxInput) maxInput.disabled = !!disabled;
    if (!PRICE_RANGE_SLIDER) return;
    PRICE_RANGE_SYNCING = true;
    PRICE_RANGE_SLIDER.disabled(!!disabled);
    PRICE_RANGE_SYNCING = false;
}

function queuePriceRangeFilterApply() {
    if (PRICE_RANGE_CHANGE_TIMEOUT) {
        clearTimeout(PRICE_RANGE_CHANGE_TIMEOUT);
    }
    PRICE_RANGE_CHANGE_TIMEOUT = setTimeout(() => {
        const filters = buildFiltersFromForm();
        applyFilters(filters, { updateUrl: true });
    }, PRICE_RANGE_APPLY_DEBOUNCE);
}

function initPriceRangeSlider() {
    const { root, slider, minInput, maxInput } = getPriceFilterElements();
    if (!root || !slider || !minInput || !maxInput || PRICE_RANGE_SLIDER) return;

    PRICE_RANGE_SLIDER = rangeSlider(slider, {
        min: 0,
        max: 0,
        step: 0.01,
        value: [0, 0],
        disabled: true,
        onInput: (value, userInteraction) => {
            if (!Array.isArray(value) || value.length < 2) return;
            const minBound = parsePriceValue(minInput.min);
            const maxBound = parsePriceValue(maxInput.max);
            if (!Number.isFinite(minBound) || !Number.isFinite(maxBound)) return;
            const [nextMin, nextMax] = clampPriceRangePair(value[0], value[1], minBound, maxBound);
            syncPriceRangeHiddenInputs(nextMin, nextMax);
            syncPriceRangeLabels(nextMin, nextMax);
            if (userInteraction && !PRICE_RANGE_SYNCING && !minInput.disabled && !maxInput.disabled) {
                queuePriceRangeFilterApply();
            }
        }
    });
}

function syncPriceRangeLabels(minValue, maxValue) {
    const { minLabel, maxLabel, minInput, maxInput } = getPriceFilterElements();
    if (minLabel) minLabel.textContent = formatPriceValue(minValue);
    if (maxLabel) maxLabel.textContent = formatPriceValue(maxValue);

    const minBound = parsePriceValue(minInput?.min);
    const maxBound = parsePriceValue(maxInput?.max);
    if (!Number.isFinite(minBound) || !Number.isFinite(maxBound) || maxBound <= minBound) {
        resetPriceRangeLabelPositions();
        return;
    }

    positionPriceValueLabel(minLabel, toPricePercent(minValue, minBound, maxBound));
    positionPriceValueLabel(maxLabel, toPricePercent(maxValue, minBound, maxBound));
}

function syncPriceRangeFromFilters(filters = null) {
    const { root, minInput, maxInput, emptyLabel } = getPriceFilterElements();
    if (!root || !minInput || !maxInput) return;
    initPriceRangeSlider();

    const minBound = parsePriceValue(minInput.min);
    const maxBound = parsePriceValue(minInput.max);
    if (!Number.isFinite(minBound) || !Number.isFinite(maxBound) || maxBound < minBound) {
        return;
    }

    const source = filters || LAST_FILTERS || buildDefaultFilters();
    let minSelected = parsePriceValue(source?.price_min);
    let maxSelected = parsePriceValue(source?.price_max);
    [minSelected, maxSelected] = clampPriceRangePair(minSelected, maxSelected, minBound, maxBound);
    syncPriceRangeSliderValues(minSelected, maxSelected);

    if (emptyLabel) {
        emptyLabel.hidden = true;
    }
}

function applyPriceRangeMeta(meta = null, filters = null) {
    const { root, minInput, maxInput, minLabel, maxLabel, emptyLabel } = getPriceFilterElements();
    if (!root || !minInput || !maxInput) return;
    initPriceRangeSlider();

    const enabled = !!meta?.enabled;
    const rangeMin = parsePriceValue(meta?.min);
    const rangeMax = parsePriceValue(meta?.max);
    const hasPrices = !!meta?.has_prices && Number.isFinite(rangeMin) && Number.isFinite(rangeMax) && rangeMax >= rangeMin;

    if (!enabled || !hasPrices) {
        setPriceRangeBounds(0, 0);
        syncPriceRangeSliderValues(0, 0);
        setPriceRangeDisabled(true);
        if (minLabel) minLabel.textContent = 'Non disponibile';
        if (maxLabel) maxLabel.textContent = 'Non disponibile';
        resetPriceRangeLabelPositions();
        if (emptyLabel) emptyLabel.hidden = false;
        return;
    }

    setPriceRangeDisabled(false);
    setPriceRangeBounds(rangeMin, rangeMax);

    const mergedFilters = {
        ...(filters || LAST_FILTERS || buildDefaultFilters()),
        price_min: parsePriceValue(meta?.selected_min),
        price_max: parsePriceValue(meta?.selected_max)
    };
    syncPriceRangeFromFilters(mergedFilters);
}

function fetchPriceRangeMeta(filters = null) {
    const { root } = getPriceFilterElements();
    if (!root) return Promise.resolve();

    const sourceFilters = filters || LAST_FILTERS || buildDefaultFilters();
    const filtersForMeta = {
        ...sourceFilters,
        price_min: null,
        price_max: null
    };

    const base = window.location.pathname;
    const qs = buildQueryFromFilters(filtersForMeta);

    const payload = new URLSearchParams({
        action: 'filter_catalog',
        nonce: WPURLS.nonce,
        filters: JSON.stringify(filtersForMeta),
        filter_page: '1',
        base_url: base,
        qs: qs,
        price_meta_only: '1'
    });

    return fetch(WPURLS.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload
    })
    .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((json) => {
        if (!json || json.success !== true) return;
        applyPriceRangeMeta(json.data?.price_range || null, sourceFilters);
    })
    .catch((err) => {
        console.error('catalog price range error:', err);
    });
}

// ==========================
// Stato
// ==========================
let LAST_FILTERS = null;
let GLOBAL_LISTENERS_BOUND = false;
let INITIAL_MODELS_HTML = '';
const PANTONE_SUGGEST_MAX = 3;
const PANTONE_SUGGEST_DEBOUNCE = 200;
let pantoneSuggestAbort = null;
let pantoneSuggestTimer = null;
const FILTERS_ACCORDION_MQ = window.matchMedia ? window.matchMedia('(max-width: 991.98px)') : null;
const FILTERS_COLOR_MQ = window.matchMedia ? window.matchMedia('(max-width: 767.98px)') : null;

function getCatalogElements() {
    return {
        grid: document.getElementById('catalogGrid'),
        loader: document.getElementById('catalogLoader')
    };
}

function cacheInitialGrid(force = false) {
    const { grid } = getCatalogElements();
    if (grid && (force || !INITIAL_MODELS_HTML)) {
        INITIAL_MODELS_HTML = grid.innerHTML;
    }
}

function startLoadingState() {
    const { grid, loader } = getCatalogElements();
    if (loader) {
        loader.classList.remove('is-hidden');
        loader.setAttribute('aria-busy', 'true');
    }
    if (grid) {
        grid.classList.add('is-hidden');
        grid.classList.remove('is-visible');
    }
}

function revealGrid(html) {
    const { grid } = getCatalogElements();
    if (!grid) return;

    if (typeof html === 'string') {
        grid.innerHTML = html;
    }

    requestAnimationFrame(() => {
        grid.classList.remove('is-hidden');
        grid.classList.add('is-visible');
    });
}

function stopLoadingState() {
    const { loader } = getCatalogElements();
    if (loader) {
        loader.classList.add('is-hidden');
        loader.setAttribute('aria-busy', 'false');
    }
}

function getPantoneSuggestionsBox() {
    const existing = document.getElementById('pantoneSuggestions');
    if (existing) return existing;
    const container = document.querySelector('.filters-list.pantone');
    if (!container) return null;
    const box = document.createElement('div');
    box.id = 'pantoneSuggestions';
    box.className = 'pantone-suggestions is-hidden';
    container.appendChild(box);
    return box;
}

function hidePantoneSuggestions() {
    const box = getPantoneSuggestionsBox();
    if (box) {
        box.classList.add('is-hidden');
        box.innerHTML = '';
    }
}

function renderPantoneSuggestions(items = []) {
    const box = getPantoneSuggestionsBox();
    const input = document.getElementById('pantoneFilterInput');
    if (!box || !input) return;

    box.innerHTML = '';
    if (!items.length) {
        box.classList.add('is-hidden');
        return;
    }

    const pantoneLabel = (window.WPURLS && WPURLS.pantone_label) ? String(WPURLS.pantone_label) : 'Pantone';
    items.slice(0, PANTONE_SUGGEST_MAX).forEach((item) => {
        const btn = document.createElement('button');
        const name = item.name || '';
        const hex = normalizeHex(item.hex || '');
        
        btn.type = 'button';
        btn.className = 'pantone-suggestion';
        btn.textContent = `${pantoneLabel} ${name}`.trim();
        btn.dataset.pantoneName = item.name || '';
        if (hex) {
            btn.style.setProperty('--pantone-color', hex);
            if (isLightHex(hex)) {
                btn.style.setProperty('--pantone-text', '#111');
            }
        }
        btn.addEventListener('click', () => {
            input.value = item.name || '';
            hidePantoneSuggestions();
            const filters = buildFiltersFromForm();
            applyFilters(filters, { updateUrl: true });
        });
        box.appendChild(btn);
    });

    box.classList.remove('is-hidden');
}

function fetchPantoneSuggestions(query) {
    if (!query) {
        hidePantoneSuggestions();
        return Promise.resolve([]);
    }

    if (pantoneSuggestAbort) pantoneSuggestAbort.abort();
    pantoneSuggestAbort = new AbortController();

    const payload = new URLSearchParams({
        action: 'pantone_suggest',
        nonce: WPURLS.nonce,
        query
    });

    return fetch(WPURLS.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload,
        signal: pantoneSuggestAbort.signal
    })
    .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
    .then(json => (json && json.success && Array.isArray(json.data?.items)) ? json.data.items : [])
    .catch((err) => {
        if (err.name === 'AbortError') return [];
        console.error('pantone_suggest error:', err);
        return [];
    })
    .then(renderPantoneSuggestions);
}

function initFiltersAccordion() {
    const root = document.getElementById('filtersCollapse');
    if (!root || root.dataset.accordionBound) return;

    const lists = Array.from(root.querySelectorAll('.filters-collapse-body > .container > .filters-list'));
    const colorFoot = root.querySelector('.filters-collapse-foot');
    const colorTitle = colorFoot ? colorFoot.querySelector('.foot-start .filters-list-title') : null;
    if (!lists.length && !colorTitle) return;

    lists.forEach((list) => {
        const title = list.querySelector('.filters-list-title');
        if (!title) return;
        if (!title.dataset.accordionBound) {
            title.setAttribute('role', 'button');
            title.setAttribute('tabindex', '0');
            title.dataset.accordionBound = '1';
        }
    });

    if (colorTitle && !colorTitle.dataset.accordionBound) {
        colorTitle.setAttribute('role', 'button');
        colorTitle.setAttribute('tabindex', '0');
        colorTitle.dataset.accordionBound = '1';
    }

    const isMobile = () => (FILTERS_ACCORDION_MQ ? FILTERS_ACCORDION_MQ.matches : false);
    const isColorMobile = () => (FILTERS_COLOR_MQ ? FILTERS_COLOR_MQ.matches : false);

    const syncState = () => {
        if (!isMobile()) {
            lists.forEach((list) => {
                list.classList.remove('is-open');
                const title = list.querySelector('.filters-list-title');
                if (title) title.removeAttribute('aria-expanded');
            });
            return;
        }

        lists.forEach((list) => {
            const title = list.querySelector('.filters-list-title');
            if (!title) return;
            if (list.classList.contains('eco-friendly')) {
                list.classList.add('is-open');
                title.setAttribute('aria-expanded', 'true');
                return;
            }
            const open = list.classList.contains('is-open');
            title.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        if (colorFoot && colorTitle) {
            if (!isColorMobile()) {
                colorFoot.classList.remove('is-open');
                colorTitle.removeAttribute('aria-expanded');
            } else {
                const open = colorFoot.classList.contains('is-open');
                colorTitle.setAttribute('aria-expanded', open ? 'true' : 'false');
            }
        }
    };

    const toggleList = (title) => {
        if (colorFoot && colorTitle && (title === colorTitle || title.closest('.filters-collapse-foot'))) {
            if (!isColorMobile()) return;
            colorFoot.classList.toggle('is-open');
            colorTitle.setAttribute('aria-expanded', colorFoot.classList.contains('is-open') ? 'true' : 'false');
            return;
        }
        if (!isMobile()) return;
        const list = title.closest('.filters-list');
        if (!list || list.classList.contains('eco-friendly')) return;
        list.classList.toggle('is-open');
        title.setAttribute('aria-expanded', list.classList.contains('is-open') ? 'true' : 'false');
    };

    root.addEventListener('click', (e) => {
        const title = e.target.closest('.filters-list-title');
        if (!title || !root.contains(title)) return;
        e.preventDefault();
        toggleList(title);
    });

    root.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const title = e.target.closest('.filters-list-title');
        if (!title || !root.contains(title)) return;
        e.preventDefault();
        toggleList(title);
    });

    if (FILTERS_ACCORDION_MQ && !root.dataset.accordionMediaBound) {
        FILTERS_ACCORDION_MQ.addEventListener('change', syncState);
        root.dataset.accordionMediaBound = '1';
    }
    if (FILTERS_COLOR_MQ && !root.dataset.accordionColorMediaBound) {
        FILTERS_COLOR_MQ.addEventListener('change', syncState);
        root.dataset.accordionColorMediaBound = '1';
    }

    root.dataset.accordionBound = '1';
    syncState();
}

// ==========================
// Filtri
// ==========================
function buildDefaultFilters() {
    return {
        model: [],
        material: [],
        finish: [],
        color: [],
        color_group: '',
        pantone: 0,
        mechanism: [],
        customization: [],
        eco_friendly: false,
        price_min: null,
        price_max: null
    };
}

function buildFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    const f = buildDefaultFilters();

    f.model       = toIntList(getParamValues(params, 'product_model'));
    f.material    = toIntList(getParamValues(params, 'product_material'));
    f.finish      = toIntList(getParamValues(params, 'product_finish'));
    f.color       = toIntList(getParamValues(params, 'product_color'));
    f.color_group = (getParamValues(params, 'product_color_group')[0] || '').trim();

    const pant = (getParamValues(params, 'pantone')[0] || '').trim();
    if (pant && pant.trim() !== '') f.pantone = pant.trim();

    f.mechanism     = toStrList(getParamValues(params, 'mechanism'));
    f.customization = toStrList(getParamValues(params, 'customization'));
    f.eco_friendly  = params.get('eco_friendly') === '1' || params.get('eco_friendly') === 'true';
    f.price_min     = parsePriceValue(getParamValues(params, 'price_min')[0] || null);
    f.price_max     = parsePriceValue(getParamValues(params, 'price_max')[0] || null);

    return f;
}

function isFiltersActive(f = {}) {
    if (!f) return false;
    return (
        f.eco_friendly ||
        (f.pantone && f.pantone !== 0 && String(f.pantone).trim() !== '') ||
        (Array.isArray(f.model) && f.model.length > 0) ||
        (Array.isArray(f.material) && f.material.length > 0) ||
        (Array.isArray(f.finish) && f.finish.length > 0) ||
        (Array.isArray(f.color) && f.color.length > 0) ||
        (Array.isArray(f.mechanism) && f.mechanism.length > 0) ||
        (Array.isArray(f.customization) && f.customization.length > 0) ||
        (f.color_group && String(f.color_group).trim() !== '') ||
        Number.isFinite(parsePriceValue(f.price_min)) ||
        Number.isFinite(parsePriceValue(f.price_max))
    );
}

function buildQueryFromFilters(filters) {
    const params = new URLSearchParams();

    if (filters.model.length)         params.set('product_model', filters.model.join(','));
    if (filters.material.length)      params.set('product_material', filters.material.join(','));
    if (filters.finish.length)        params.set('product_finish', filters.finish.join(','));
    if (filters.color.length)         params.set('product_color', filters.color.join(','));
    if (filters.color_group)          params.set('product_color_group', filters.color_group);

    if (filters.pantone && String(filters.pantone).trim() !== '' && filters.pantone !== 0) {
        params.set('pantone', String(filters.pantone).trim());
    }

    if (filters.mechanism.length)     params.set('mechanism', filters.mechanism.join(','));
    if (filters.customization.length) params.set('customization', filters.customization.join(','));
    if (filters.eco_friendly)         params.set('eco_friendly', '1');
    if (Number.isFinite(parsePriceValue(filters.price_min))) params.set('price_min', Number(parsePriceValue(filters.price_min)).toFixed(2));
    if (Number.isFinite(parsePriceValue(filters.price_max))) params.set('price_max', Number(parsePriceValue(filters.price_max)).toFixed(2));

    return params.toString(); // NB: non include filter_page
}

function syncUIFromFilters(filters) {
    const root = document.getElementById('filtersCollapse');
    if (!root) return;

    // reset check/radio
    root.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(el => el.checked = false);

    // toggle semplici
    if (filters.eco_friendly) {
        const el = root.querySelector('#checkEcoFriendly');
        if (el) el.checked = true;
    }
    const featuredEco = document.getElementById('checkEcoFriendlyFeatured');
    if (featuredEco) featuredEco.checked = Boolean(filters.eco_friendly);

    if (filters.mechanism.includes('retractable')) {
        const el = root.querySelector('#checkMechanismRetractable');
        if (el) el.checked = true;
    }
    if (filters.mechanism.includes('twist')) {
        const el = root.querySelector('#checkMechanismTwist');
        if (el) el.checked = true;
    }
    if (filters.customization.includes('barrel_full_printable')) {
        const el = root.querySelector('#checkBarrelFullPrintable');
        if (el) el.checked = true;
    }

    // checkbox con data-type (model/material/finish/color)
    const selected = new Set([
        ...filters.model.map(v => `model:${v}`),
        ...filters.material.map(v => `material:${v}`),
        ...filters.finish.map(v => `finish:${v}`),
        ...filters.color.map(v => `color:${v}`)
    ]);
    root.querySelectorAll('input[type="checkbox"][data-type]').forEach(cb => {
        const type = cb.getAttribute('data-type');
        const val  = parseInt(cb.value, 10);
        if (type && Number.isFinite(val) && selected.has(`${type}:${val}`)) {
            cb.checked = true;
        }
    });

    // radio colori aggregati (color_group)
    if (filters.color_group) {
        const radios = root.querySelectorAll('.colors-filter-bar input[type="radio"]');
        for (const r of radios) {
            if (r.value === filters.color_group) {
                r.checked = true;
                break;
            }
        }
    }

    // pantone
    const pantoneInput = document.getElementById('pantoneFilterInput');
    if (pantoneInput) pantoneInput.value = (filters.pantone && filters.pantone !== 0) ? String(filters.pantone) : '';

    syncPriceRangeFromFilters(filters);
}

function cascadeEcoFriendlyMaterials(root = null) {
    const scope = root || document.getElementById('filtersCollapse');
    if (!scope) return;
    const ecoFriendlyInput = scope.querySelector('#checkEcoFriendly');
    if (!ecoFriendlyInput || !ecoFriendlyInput.checked) return;

    scope.querySelectorAll('input[type="checkbox"][data-type="material"][data-eco-material="1"]').forEach((checkbox) => {
        checkbox.checked = true;
    });
}

function buildFiltersFromForm() {
    const filters = buildDefaultFilters();
    const root = document.getElementById('filtersCollapse');
    if (!root) return filters;

    // checkbox
    root.querySelectorAll('input[type="checkbox"]:checked').forEach(checkbox => {
        switch (checkbox.id) {
            case 'checkEcoFriendly': filters.eco_friendly = true; break;
            case 'checkMechanismRetractable': filters.mechanism.push('retractable'); break;
            case 'checkMechanismTwist':       filters.mechanism.push('twist'); break;
            case 'checkBarrelFullPrintable':  filters.customization.push('barrel_full_printable'); break;
            default: {
                const type  = checkbox.getAttribute('data-type');
                const value = parseInt(checkbox.value, 10);
                if (type && Object.prototype.hasOwnProperty.call(filters, type) && Number.isFinite(value)) {
                    filters[type].push(value);
                }
            }
        }
    });

    // radio gruppi colore
    const selectedColorGroup = root.querySelector('.colors-filter-bar input[name="radioColorGroup"]:checked');
    if (selectedColorGroup) {
        filters.color_group = selectedColorGroup.value;
    }

   

    // pantone
    const pantoneInput = document.getElementById('pantoneFilterInput');
    if (pantoneInput) {
        const v = pantoneInput.value.trim();
        if (v) filters.pantone = v;
    }

    const priceMinInput = document.getElementById('priceRangeMinInput');
    const priceMaxInput = document.getElementById('priceRangeMaxInput');
    if (priceMinInput && priceMaxInput && !priceMinInput.disabled && !priceMaxInput.disabled) {
        const minBound = parsePriceValue(priceMinInput.min);
        const maxBound = parsePriceValue(priceMaxInput.max);
        let minSelected = parsePriceValue(priceMinInput.value);
        let maxSelected = parsePriceValue(priceMaxInput.value);

        if (Number.isFinite(minBound) && Number.isFinite(maxBound)) {
            if (!Number.isFinite(minSelected)) minSelected = minBound;
            if (!Number.isFinite(maxSelected)) maxSelected = maxBound;

            minSelected = Math.max(minBound, Math.min(minSelected, maxBound));
            maxSelected = Math.max(minBound, Math.min(maxSelected, maxBound));
            if (minSelected > maxSelected) {
                minSelected = maxSelected;
            }

            if (minSelected > minBound) {
                filters.price_min = Number(minSelected.toFixed(2));
            }
            if (maxSelected < maxBound) {
                filters.price_max = Number(maxSelected.toFixed(2));
            }
        }
    }

    return filters;
}

// ==========================
// Paginazione: filter_page
// ==========================
function getFilterPageFromHref(href) {
    if (!href) return 1;
    const url = new URL(href, window.location.origin);
    const p = url.searchParams.get('filter_page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
}
function getFilterPageFromLocation() {
    const url = new URL(window.location.href);
    const p = url.searchParams.get('filter_page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
}
function buildUrlWithFilterPage(filters, page) {
    const base = window.location.pathname;
    const qs   = buildQueryFromFilters(filters); // senza filter_page
    const url  = new URL(base, window.location.origin);

    if (qs) {
        qs.split('&').forEach(kv => {
            if (!kv) return;
            const [k, v] = kv.split('=');
            if (k) url.searchParams.set(k, v ?? '');
        });
    }
    if (page > 1) url.searchParams.set('filter_page', String(page));
    else url.searchParams.delete('filter_page');

    return url.pathname + (url.search ? url.search : '');
}

// ==========================
// AJAX
// ==========================
function ajaxApplyFilters(filters, page = 1) {
    const shouldLoadVariants = isFiltersActive(filters);
    startLoadingState();

    // Se non ci sono filtri attivi, mostra i modelli pre-renderizzati
    if (!shouldLoadVariants) {
        revealGrid(INITIAL_MODELS_HTML);
        hideResultsCounter();
        stopLoadingState();
        return fetchPriceRangeMeta(filters);
    }

    const base = window.location.pathname;
    const qs   = buildQueryFromFilters(filters);

    const payload = new URLSearchParams({
        action: 'filter_catalog',
        nonce: WPURLS.nonce,
        filters: JSON.stringify(filters),
        filter_page: String(page),
        base_url: base,
        qs: qs
    });

    return fetch(WPURLS.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload
    })
    .then(async (response) => {
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`HTTP ${response.status}: ${text.slice(0, 200)}`);
        }
        return response.json();
    })
    .then((json) => {
        if (!json || json.success !== true) throw new Error('Risposta AJAX non valida.');
        const html = json.data?.html ?? '';
        const count = json.data?.count ?? 0;
        const priceRange = json.data?.price_range ?? null;
        let currentFilters = LAST_FILTERS || buildFiltersFromForm();

        revealGrid(html);
        applyPriceRangeMeta(priceRange, currentFilters);
        currentFilters = buildFiltersFromForm();
        LAST_FILTERS = currentFilters;

        if (isFiltersActive(currentFilters)) {
            updateResultsCounter(count);
        } else {
            hideResultsCounter();
        }

        stopLoadingState();
    })
    .catch((err) => {
        console.error('filter_catalog error:', err);
        stopLoadingState();
    });
}

// ==========================
// Orchestratori
// ==========================
function applyFiltersAndPersist(filters, page = 1, { push = true } = {}) {
    LAST_FILTERS = filters;
    if (push) {
        const url = buildUrlWithFilterPage(filters, page);
        window.history.pushState({ filters, page }, '', url);
    }
    return ajaxApplyFilters(filters, page);
}
function applyFilters(filters, { updateUrl = false } = {}) {
    const page = 1; // reset a pagina 1 quando cambi i filtri
    if (updateUrl) return applyFiltersAndPersist(filters, page, { push: true });
    else           return applyFiltersAndPersist(filters, page, { push: false });
}

function initCatalog() {
    cacheInitialGrid(true);
    const urlFilters = buildFiltersFromURL();
    const hasAny = isFiltersActive(urlFilters);

    const initialPage = getFilterPageFromLocation();
    LAST_FILTERS = hasAny ? urlFilters : buildDefaultFilters();

    if (hasAny) syncUIFromFilters(urlFilters);
    const needsAjax = hasAny;

    if (needsAjax) {
        startLoadingState();
        applyFiltersAndPersist(LAST_FILTERS, initialPage, { push: false })
            .finally(() => {
                initResultsCounter(isFiltersActive(LAST_FILTERS));
            });
    } else {
        revealGrid(INITIAL_MODELS_HTML);
        stopLoadingState();
        initResultsCounter(false);
        fetchPriceRangeMeta(LAST_FILTERS);
    }

    const filtersCollapse = document.getElementById('filtersCollapse');
    if (filtersCollapse && !filtersCollapse.dataset.changeBound) {
        const ecoFriendlyInput = filtersCollapse.querySelector('#checkEcoFriendly');
        if (ecoFriendlyInput && !ecoFriendlyInput.dataset.ecoCascadeBound) {
            ecoFriendlyInput.addEventListener('change', () => {
                cascadeEcoFriendlyMaterials(filtersCollapse);
            });
            ecoFriendlyInput.dataset.ecoCascadeBound = '1';
        }

        filtersCollapse.addEventListener('change', (e) => {
            const input = e.target.closest('input[type="checkbox"], input[type="radio"]');
            if (!input) return;
            const filters = buildFiltersFromForm();
            applyFilters(filters, { updateUrl: true });
        });
        filtersCollapse.dataset.changeBound = '1';
    }

    cascadeEcoFriendlyMaterials(filtersCollapse);

    const pantoneInput = document.getElementById('pantoneFilterInput');
    if (pantoneInput && !pantoneInput.dataset.bound) {
        pantoneInput.addEventListener('input', () => {
            const term = pantoneInput.value.trim();
            clearTimeout(pantoneSuggestTimer);
            pantoneSuggestTimer = setTimeout(() => {
                fetchPantoneSuggestions(term);
            }, PANTONE_SUGGEST_DEBOUNCE);
        });
        pantoneInput.addEventListener('focus', () => {
            const term = pantoneInput.value.trim();
            if (term) fetchPantoneSuggestions(term);
        });
        pantoneInput.addEventListener('change', () => {
            hidePantoneSuggestions();
            const filters = buildFiltersFromForm();
            applyFilters(filters, { updateUrl: true });
        });
        pantoneInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                hidePantoneSuggestions();
                const filters = buildFiltersFromForm();
                applyFilters(filters, { updateUrl: true });
            }
        });
        pantoneInput.dataset.bound = '1';
    }

    const resetBtn = document.getElementById('removeCatalogFiltersBtn');
    if (resetBtn && !resetBtn.dataset.bound) {
        resetBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const defaultFilters = buildDefaultFilters();
            LAST_FILTERS = defaultFilters;
            syncUIFromFilters(defaultFilters);
            hideResultsCounter();

            const filtersCollapse = document.getElementById('filtersCollapse');
            if (filtersCollapse && window.bootstrap) {
                const bsCollapse = window.bootstrap.Collapse.getInstance(filtersCollapse);
                if (bsCollapse) bsCollapse.hide();
            }
            applyFiltersAndPersist(defaultFilters, 1, { push: true });
        });
        resetBtn.dataset.bound = '1';
    }

    initPriceRangeSlider();

}

// ==========================
// Deleghe globali (una sola volta)
// ==========================
function bindGlobalListenersOnce() {
    if (GLOBAL_LISTENERS_BOUND) return;

    // Click su paginazione (delegato)
    document.addEventListener('click', (e) => {
        const anchor = e.target.closest('.pagination a, .pagination button');
        if (!anchor) return;

        // se non ci sono filtri attivi, lascio comportamento default (paginazione server-side)
        const currentFilters = LAST_FILTERS || buildFiltersFromForm();
        if (!isFiltersActive(currentFilters)) {
            return;
        }

        // rispetta apertura in nuova scheda/shortcut
        if (anchor.tagName === 'A' && (anchor.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
            return;
        }

        e.preventDefault();
        const pageAttr = anchor.getAttribute('data-paged');
        const page = pageAttr ? Math.max(1, parseInt(pageAttr, 10)) : getFilterPageFromHref(anchor.getAttribute('href'));
        const filters = LAST_FILTERS || buildFiltersFromForm();
        applyFiltersAndPersist(filters, page, { push: true });
    });

    // click fuori dalle suggestion pantone
    document.addEventListener('click', (e) => {
        if (!e.target.closest('#pantoneFilterInput') && !e.target.closest('#pantoneSuggestions')) {
            hidePantoneSuggestions();
        }
    });

    // Popstate
    window.addEventListener('popstate', (e) => {
        const filters = e.state?.filters || buildFiltersFromURL();
        const page    = e.state?.page    || getFilterPageFromLocation();

        if (!isFiltersActive(filters)) {
            LAST_FILTERS = buildDefaultFilters();
            syncUIFromFilters(LAST_FILTERS);
            revealGrid(INITIAL_MODELS_HTML);
            hideResultsCounter();
            stopLoadingState();
            fetchPriceRangeMeta(LAST_FILTERS);
            return;
        }

        LAST_FILTERS = filters;
        syncUIFromFilters(filters);
        ajaxApplyFilters(filters, page);
    });

    GLOBAL_LISTENERS_BOUND = true;
}

// ==========================
// Avvio
// ==========================
bindGlobalListenersOnce();
onReady(() => {
    initCatalog();
    initFiltersAccordion();
});
